import { Button, Classes, Dialog, Intent, MenuItem } from '@blueprintjs/core';
import * as React from 'react';
import { Translate } from '../WithTranslate';
import { TLCode } from '../../../common/enums';
import { Instance } from 'mobx-state-tree';
import { Pharmacy } from '../../../store/models';
import { IItemRendererProps, MultiSelect } from '@blueprintjs/select';
import { useCallback, useMemo } from 'react';

interface SharedDutiesPharmaciesDialogProps {
  isOpen: boolean;
  pharmacyToLink: Instance<typeof Pharmacy>;
  pharmacies: Array<Instance<typeof Pharmacy>>;
  close: () => void;
  linkPharmacies: (pharmacyIds: number[]) => void;
  unlinkPharmacies: (pharmacyIds: number[]) => void;
  translate: Translate;
}

export const SharedDutiesPharmaciesDialog = ({
  isOpen,
  close,
  translate,
  linkPharmacies,
  unlinkPharmacies,
  pharmacies,
  pharmacyToLink,
}: SharedDutiesPharmaciesDialogProps) => {
  const initialStateOfPharmaciesLinked = useMemo(() => {
    if (pharmacyToLink.sharedDutiesGroupId) {
      return pharmacies
        .filter((p) => p.sharedDutiesGroupId === pharmacyToLink.sharedDutiesGroupId)
        .map((p) => p.pharmacyId);
    }
    return [];
  }, [pharmacies, pharmacyToLink.sharedDutiesGroupId]);

  const [pharmaciesToLink, setPharmaciesToLink] = React.useState<number[]>(initialStateOfPharmaciesLinked);
  const [pharmaciesToUnlink, setPharmaciesToUnlink] = React.useState<number[]>([]);

  const pharmacyItemDisplayed = useMemo(() => (p: Instance<typeof Pharmacy>) => `${p.apbNumber} - ${p.name}`, []);

  const pharmacyItemRenderer = useCallback(
    (item: Instance<typeof Pharmacy>, { handleClick, modifiers }: IItemRendererProps) => {
      if (!modifiers.matchesPredicate) return null;
      return (
        <MenuItem
          active={modifiers.active}
          key={item.pharmacyId}
          onClick={handleClick}
          text={pharmacyItemDisplayed(item)}
        />
      );
    },
    [pharmacyItemDisplayed],
  );

  const onPharmacySelected = useCallback(
    (item: Instance<typeof Pharmacy>) => {
      setPharmaciesToLink((prev) => [...prev, item.pharmacyId]);
      setPharmaciesToUnlink((prev) => [...prev.filter((id) => id !== item!.pharmacyId)]);
    },
    [setPharmaciesToLink, setPharmaciesToUnlink],
  );

  const onPharmacyRemoved = useCallback((value: string, index: number) => {
    const pharmacyToRemove = pharmacies.find((p) => pharmacyItemDisplayed(p) === value);
    if (!pharmacyToRemove) return;
    setPharmaciesToLink((prev) => [...prev.filter((id) => id !== pharmacyToRemove!.pharmacyId)]);
    if (initialStateOfPharmaciesLinked.includes(pharmacyToRemove!.pharmacyId)) {
      setPharmaciesToUnlink((prev) => [...prev, pharmacyToRemove!.pharmacyId]);
    }
  }, []);

  const save = useCallback(() => {
    if (pharmaciesToLink.length > 0) {
      const finalPharmaciesToLink = pharmaciesToLink;
      linkPharmacies([...finalPharmaciesToLink, pharmacyToLink.pharmacyId]);
    }
    if (pharmaciesToUnlink.length > 0) {
      const finalPharmaciesToUnlink = pharmaciesToUnlink.filter((id) => initialStateOfPharmaciesLinked.includes(id));
      unlinkPharmacies(finalPharmaciesToUnlink);
    }
    close();
  }, [pharmaciesToLink, pharmaciesToUnlink, linkPharmacies, unlinkPharmacies, close]);

  return (
    <Dialog
      isOpen={isOpen}
      title={translate(TLCode.PHARMACY_SharedDutiesGroup_Dialog_Title, { pharmacyName: pharmacyToLink.name })}
      onClose={close}
      canOutsideClickClose={false}
    >
      <div className={Classes.DIALOG_BODY}>
        <MultiSelect
          itemRenderer={pharmacyItemRenderer}
          items={pharmacies.filter((r) => !pharmaciesToLink.includes(r.pharmacyId))}
          onItemSelect={onPharmacySelected}
          tagRenderer={(item: Instance<typeof Pharmacy>) => pharmacyItemDisplayed(item)}
          itemPredicate={(query, item) => {
            return pharmacyItemDisplayed(item)
              .toLocaleLowerCase()
              .includes(query.toLocaleLowerCase());
          }}
          tagInputProps={{
            onRemove: onPharmacyRemoved,
          }}
          selectedItems={pharmacies.filter((r) => pharmaciesToLink.includes(r.pharmacyId))}
          resetOnSelect
          placeholder={translate(TLCode.PHARMACY_SharedDutiesGroup_Dialog_Select)}
        />
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            onClick={() => {
              save();
            }}
            intent={Intent.PRIMARY}
            text={translate(TLCode.GENERAL_Confirm)}
          />
          <Button
            onClick={() => {
              close();
            }}
            intent={Intent.NONE}
            text={translate(TLCode.GENERAL_Cancel)}
          />
        </div>
      </div>
    </Dialog>
  );
};
